<template>
  <!-- Inline SVG area chart (per the design's AnxTrend) — renders deterministically,
       no chart.js. Time flows left→right; the peak day is annotated in-chart. -->
  <svg :viewBox="`0 0 ${W} ${H}`" class="block w-full" role="img">
    <defs>
      <linearGradient :id="gradientId" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stop-color="#8864ee" stop-opacity="0.25"/>
        <stop offset="100%" stop-color="#8864ee" stop-opacity="0"/>
      </linearGradient>
    </defs>

    <path :d="areaPath" :fill="`url(#${gradientId})`"/>
    <path :d="linePath" fill="none" stroke="#8864ee" stroke-width="2" stroke-linejoin="round" vector-effect="non-scaling-stroke"/>

    <template v-if="peakPoint">
      <line
        :x1="peakPoint.x" :x2="peakPoint.x" :y1="peakPoint.y" :y2="H - PAD_B"
        stroke="#c3b9f9" stroke-dasharray="2,3"/>
      <circle :cx="peakPoint.x" :cy="peakPoint.y" r="4" fill="#fff" stroke="#6831d0" stroke-width="2.2"/>
      <text
        :x="peakLabelLeft ? peakPoint.x - 8 : peakPoint.x + 8"
        :y="Math.max(peakPoint.y - 9, 11)"
        :text-anchor="peakLabelLeft ? 'end' : 'start'"
        style="font-size: 11.5px; font-weight: 700; fill: #5b21b6">
        {{ peakLabel }}
      </text>
    </template>
  </svg>
</template>

<script setup lang="ts">
import {computed} from 'vue';

const props = defineProps<{
  values: number[];
  peakIdx?: number;
  peakLabel?: string;
}>();

const W = 360;
const H = 120;
const PAD_X = 6;
const PAD_T = 28;
const PAD_B = 8;

// unique per instance so multiple charts on one page don't share defs
const gradientId = `revTrendG-${Math.random().toString(36).slice(2, 8)}`;

const point = (v: number, i: number): {x: number; y: number} => {
  const denom = Math.max(props.values.length - 1, 1);
  const max = Math.max(...props.values, 1);
  return {
    x: PAD_X + (W - PAD_X * 2) * (i / denom),
    y: H - PAD_B - (v / max) * (H - PAD_T - PAD_B),
  };
};

const linePath = computed(() =>
  props.values
    .map((v, i) => {
      const {x, y} = point(v, i);
      return `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' '));

const areaPath = computed(() =>
  `${linePath.value} L${(W - PAD_X).toFixed(1)},${H - PAD_B} L${PAD_X},${H - PAD_B} Z`);

const peakPoint = computed(() => {
  const i = props.peakIdx ?? -1;
  if (i < 0 || i >= props.values.length || !props.peakLabel) return null;
  return point(props.values[i], i);
});

const peakLabelLeft = computed(() => (peakPoint.value?.x ?? 0) > W * 0.55);
</script>
